import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { appsApi, type App } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

export function AppCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<App>) => appsApi.create(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      navigate(`/apps/${result.id}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    createMutation.mutate({
      name: formData.name,
      description: formData.description,
      slug: formData.name.toLowerCase().replace(/\s+/g, '-'),
      status: 'draft',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/apps')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Nouvelle Application
          </h2>
          <p className="text-sm text-secondary-500">
            Créez une nouvelle application no-code
          </p>
        </div>
      </div>

      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-xl border border-secondary-200 bg-white p-6 dark:border-secondary-800 dark:bg-secondary-900">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom de l'application *</Label>
                <Input
                  id="name"
                  placeholder="Mon Application"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Description de l'application..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                />
              </div>
            </div>
          </div>

          {createMutation.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm text-red-700 dark:text-red-400">
                Erreur: {(createMutation.error as Error).message}
              </p>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/apps')}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || !formData.name.trim()}
            >
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer l'application
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AppCreatePage;
